/**
 * PGSS-5453 — which workflow actions the View Details popup shows.
 *
 * The list row and the View Details popup are rendered by the SAME component
 * (`OrdersRowActions`, `OpenOrdersRowAction`); `popupButtons` only decides whether each action
 * paints as an icon or as a button. The action set itself is looked up once, from
 * (stage, row status), so it is status-correct by construction and both surfaces start from the
 * same array.
 *
 * Everything that made them disagree happened afterwards, in the popup-only render path:
 *
 * 1. A running `count` capped the popup at the first three actions. The rest were dropped with no
 *    way to reach them. The row has no cap, so the row and the popup diverged the moment a status
 *    had four configured actions.
 *
 * 2. The overflow menu meant to carry the remainder was fed `actions.slice(3)` — an index into the
 *    RAW action list — while `count` counted only the actions that had survived `showActions` and
 *    `checkShow`. Those are two different lists. An action rejected by the status filter could
 *    still appear in the menu, and an action dropped by the cap could appear in neither. Both
 *    halves of the ticket — "extra actions are displayed that are not applicable to the current
 *    status" and "status-related actions are missing" — fall out of that one mismatch, which is
 *    why they were reported together.
 *
 * 3. `checkShow` returned true for everything on a manage user, so View Order / View Quote /
 *    View Rfq were offered as buttons inside the popup that was already showing that order.
 *
 * `splitPopupActions` closes (1) and (2) by deriving both halves from one filtered list: `inline`
 * and `overflow` are slices of the same array, so nothing can land in neither, and nothing the
 * filter rejected can reach the menu. `isViewAction` closes (3).
 *
 * The `filter(Boolean)` is not defensive padding. `rearrangedActions` is built by fixed index
 * (`[actions[1], actions[2], actions[3]]`), so it carries `undefined` holes whenever the configured
 * set is shorter than those indices assume, and the map that consumes it destructures every entry.
 */

/** How many actions the popup shows as buttons before the rest move into the overflow menu. */
export const POPUP_INLINE_LIMIT = 3;

/**
 * Actions that open the record the View Details popup is already showing. They are never
 * applicable inside it — for any role — and stay on the list row, the surface they were
 * configured for.
 *
 * The list is fixed rather than a `VIEW_` prefix test, because most VIEW_ actions open something
 * ELSE about the record and belong in the popup: VIEW_NOTES, VIEW_SUPPLIER_INVOICE,
 * VIEW_SHIPPING_INFO, and VIEW_INVOICE on an order row.
 *
 * REVIEW_ORDER is deliberately NOT here, though the first pass at this ticket put it here. It does
 * not open the record; it opens the approve / request-revision / reject dialog, and for a customer
 * it is the only route to those. Suppressing it as if it were a view action took the approval
 * flow out of the popup entirely on the one screen where the customer needs it. It is still row-
 * only, but for the reason the other REVIEW_ actions are: `showActions` swaps it for the approval
 * buttons themselves once we are inside a popup.
 */
const VIEW_ACTIONS = ['VIEW_ORDER', 'VIEW_QUOTE', 'VIEW_RFQ', 'VIEW_SHIPMENT'];

/**
 * @param uiId          the action being considered
 * @param ownViewUiId   the list's own default view action, `VIEW_<stage>` — VIEW_INVOICE on the
 *                      invoice list, VIEW_RFD on completed. That one is redundant in ITS OWN
 *                      popup and nowhere else, which a fixed list cannot express: VIEW_INVOICE is
 *                      worth having on an order.
 */
export const isViewAction = (uiId, ownViewUiId) =>
  VIEW_ACTIONS.includes(uiId) || (Boolean(ownViewUiId) && uiId === ownViewUiId);

/**
 * Split one action set into what the popup renders inline and what its overflow menu carries.
 *
 * @param actions    the configured action set, in the order it should be offered
 * @param isVisible  the caller's per-action rule (status filters, role filters, `isViewAction`)
 * @param limit      how many to render inline; anything falsy or non-positive means "no cap"
 */
export const splitPopupActions = (actions, isVisible, limit = POPUP_INLINE_LIMIT) => {
  const visible = (Array.isArray(actions) ? actions : []).filter(
    (action) => action && isVisible(action)
  );
  const inlineLimit = Number.isInteger(limit) && limit > 0 ? limit : visible.length;

  return { inline: visible.slice(0, inlineLimit), overflow: visible.slice(inlineLimit) };
};

/**
 * PGSS-5501 / PGSS-5470 — dialog stages under which a CUSTOMER's approval decisions open the
 * notes / document upload dialog straight from the View Details popup.
 *
 * `/awaiting-approval` mounts `<Orders type="approval" />`, so the same orders the Orders page
 * opens under 'order' open here under 'approval'. PGSS-5453 let the approval actions APPEAR in that
 * popup, but they still ran the row's handler, which opens the order details a second time with a
 * bare submit button — no documents, and a note box only on some of them. The Orders page popup
 * was right because its stage was in this list.
 */
const CUSTOMER_APPROVAL_POPUP_STAGES = ['order', 'quote', 'approval'];

export const isCustomerApprovalPopupStage = (stage) =>
  CUSTOMER_APPROVAL_POPUP_STAGES.includes(stage);

/**
 * PGSS-5470 — actions that, clicked inside the plain View Details popup, open the approval dialog
 * (optional note to approve; note and documents to request a revision) instead of re-running the
 * row's handler, which re-opens the order details with a note box.
 *
 * Only the drawing DECISIONS. Two neighbours are left out on purpose, because neither opens a
 * dialog at all -- both run on their first click from the popup (PGSS-5471,
 * `firstClickPopupAction`):
 *
 * - SEND_ORDER_FOR_DRAWING_APPROVAL ("Send Drawing for Approval").
 * - APPROVE_DRAWING and APPROVE_VAS_PRICE are NOT drawing decisions for staff, whatever the id
 *   says: staff "Approve Order" at Out for Approval is configured as APPROVE_DRAWING, and staff
 *   "Approve Vas Price" as APPROVE_VAS_PRICE. The customer's copies are covered by
 *   `isCustomerApprovalPopupStage` instead.
 */
const DRAWING_DECISION_ACTIONS = [
  'APPROVE_DRAWING_ORDER',
  'REQUEST_REVISION_FOR_DRAWING_ORDER',
  'REJECT_DRAWING_ORDER',
];

export const opensApprovalDialogFromPopup = (uiId) => DRAWING_DECISION_ACTIONS.includes(uiId);

/**
 * PGSS-5471 — actions that run on their FIRST click from the plain View Details popup.
 *
 * Clicked from a list row, each of these opens a second view of the order (the "review step") and
 * only calls the API from there. Clicked from inside View Details that second view is the same
 * order again, so QA saw the popup reopen and had to click the action twice. The PSG team decided
 * View Details itself is enough: the action runs immediately, and the little each second view
 * collected is offered inside View Details instead.
 *
 * What the second view collected, per action (checked against ViewOrderDialog):
 * - REVIEW_QUOTE at Requested ("Send for Quote Approval"): which drawings to include (tick boxes)
 * - SEND_FOR_DRAWING_APPROVAL at Pending Approval / Revision Requested ("Send for Order Approval"):
 *   optional documents per line
 * - everything else in the table: nothing. No note box (their names are not in the dialog's note
 *   list), no fields, just the order again and a submit button.
 *
 * Matched by **id or configured name**, always together with the status: the same action is
 * configured under different ids in different environments (locally "Send for Order Approval" is
 * SEND_FOR_DRAWING_APPROVAL, and the code also handles a SEND_FOR_ORDER_APPROVAL id), while the name
 * QA reports is the one the button shows. Never by id or name alone, because both are shared with
 * actions QA did not report and that are left as they are:
 * - APPROVE_DRAWING is also "Approve to Ship" (Awaiting to Ship) and the customer's "Send for Review"
 * - SEND_FOR_DRAWING_APPROVAL is also "Send for Drawing Approval" (Design Layout, Waiting on Custom
 *   Design), where a document on every line is REQUIRED
 * - REVIEW_QUOTE is also "Review Quote" (Out for Quote Approval)
 *
 * `staffOnly` because the customer's copies of Approve Order / Approve Vas Price are approval
 * DECISIONS with a note (PGSS-5501, `isCustomerApprovalPopupStage`), and never reach this router.
 */
const FIRST_CLICK_POPUP_ACTIONS = [
  {
    uiIds: ['REVIEW_QUOTE', 'SEND_FOR_QUOTE_APPROVAL'],
    names: ['Send for Quote Approval'],
    stage: 'quote',
    statuses: ['QUOTE_REQUESTED'],
    staffOnly: true,
    input: 'drawings',
  },
  {
    uiIds: ['SEND_FOR_DRAWING_APPROVAL', 'SEND_FOR_ORDER_APPROVAL'],
    names: ['Send for Order Approval'],
    stage: 'order',
    statuses: ['ORDER_PENDING_APPROVAL', 'ORDER_REVISION_REQUESTED'],
    staffOnly: true,
    input: 'documents',
  },
  {
    uiIds: ['APPROVE_DRAWING'],
    names: ['Approve Order'],
    stage: 'order',
    statuses: ['ORDER_OUT_FOR_APPROVAL'],
    staffOnly: true,
  },
  {
    uiIds: ['APPROVE_VAS_PRICE'],
    names: ['Approve Vas Price', 'Approve VAS Price'],
    stage: 'order',
    statuses: ['ORDER_OUT_FOR_VAS_PRICE_APPROVAL'],
    staffOnly: true,
  },
  {
    uiIds: ['SEND_ORDER_FOR_DRAWING_APPROVAL'],
    names: ['Send Drawing for Approval'],
    stage: 'order',
    statuses: ['ORDER_DRAWING_RECEIVED_FROM_SUPPLIER'],
    staffOnly: true,
  },
  {
    uiIds: ['MAKE_IN_PROGRESS'],
    names: ['Move to Production'],
    stage: null,
    statuses: ['ORDER_RECEIVED', 'ORDER_STONE_SHIPMENT_PENDING'],
  },
  {
    uiIds: ['CANCEL_INVOICE'],
    names: ['Cancel Invoice'],
    stage: 'invoice',
    statuses: ['ORDER_INVOICED'],
    staffOnly: true,
  },
  {
    uiIds: ['SEND_REMINDER'],
    names: ['Send Reminder'],
    stage: 'invoice',
    statuses: ['ORDER_INVOICED', 'ORDER_PARTIAL_PAYMENT'],
    staffOnly: true,
  },
];

const matchesRule = (rule, { stage, statusCode, isCustomerUser }) =>
  rule.statuses.includes(statusCode) &&
  (rule.stage === null || rule.stage === stage) &&
  !(rule.staffOnly && isCustomerUser);

/**
 * @param action   the action clicked inside the popup: `{ ui_id, name }` (a bare id is also accepted)
 * @param context  `{ stage, statusCode, isCustomerUser }` of the record the popup shows. `stage` is the
 *                 popup's stage; Move to Production matches any, since the supplier's lists open it
 *                 under more than one.
 * @return the rule — `input` is 'drawings', 'documents' or undefined — or null to keep the row's handler
 */
export const firstClickPopupAction = (action, context = {}) => {
  const uiId = typeof action === 'string' ? action : action?.ui_id;
  const name = typeof action === 'string' ? undefined : action?.name;
  return (
    FIRST_CLICK_POPUP_ACTIONS.find(
      (rule) =>
        (rule.uiIds.includes(uiId) || (Boolean(name) && rule.names.includes(name)))
        && matchesRule(rule, context)
    ) || null
  );
};

/**
 * What View Details should offer so a first-click action has what it needs: 'drawings' (tick boxes
 * on the drawings), 'documents' (the add-documents icon on each line) or null.
 */
export const popupInlineInput = (context = {}) =>
  FIRST_CLICK_POPUP_ACTIONS.find((rule) => rule.input && matchesRule(rule, context))?.input || null;

/** The document keys a line can carry, whichever of drawing or proof the order uses. */
const LINE_DOCUMENT_KEYS = ['drawing_documents', 'new_drawing_documents', 'proof_documents', 'new_proof_documents'];

/**
 * The payload "Send for Order Approval" sends from View Details: the same shape its second view sent
 * (one entry per line, from `getApprovalResetData`, plus whatever documents were added to that line).
 * Built explicitly so nothing else the popup's form holds for a line is sent with it.
 */
export const buildInlineDocumentsPayload = (lines, formValues) => ({
  note: formValues?.note,
  order_details: (Array.isArray(lines) ? lines : []).map((line, index) => {
    const entered = formValues?.order_details?.[index] || {};
    const documents = Object.fromEntries(
      LINE_DOCUMENT_KEYS.filter((key) => entered[key] !== undefined).map((key) => [key, entered[key]])
    );
    return { id: line?.id, vas: line?.vas, showDocuments: false, ...documents };
  }),
});
